import React, { useState, useEffect } from 'react';
import { Dialog, Box, Typography, TextField, Button, Checkbox, FormControlLabel } from '@mui/material';
import { PaymentProvider, CreateProviderDto, UpdateProviderDto } from 'types/paymentProvider';
import styles from './CreateEditProviderModal.module.scss';

interface CreateEditProviderModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateProviderDto | UpdateProviderDto) => void;
  initialData?: PaymentProvider | null;
  isLoading?: boolean;
}

const CreateEditProviderModal: React.FC<CreateEditProviderModalProps> = ({
  open,
  onClose,
  onSave,
  initialData,
  isLoading = false,
}) => {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (initialData) {
      setCode(initialData.code || '');
      setName(initialData.name || '');
      setDescription(initialData.description || '');
      setIsActive(initialData.isActive || false);
    } else {
      setCode('');
      setName('');
      setDescription('');
      setIsActive(false);
    }
  }, [initialData, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (initialData) {
      // Edit mode (code is not updated per backend instructions)
      onSave({ name, description, isActive });
    } else {
      // Create mode
      onSave({ code, name, description });
    }
  };

  const isEditMode = !!initialData;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: styles.dialogPaper }}
      disableRestoreFocus
      maxWidth="sm"
      fullWidth
    >
      <Typography variant="h6" className={styles.title}>
        {isEditMode ? 'Edit Payment Provider' : 'Create Payment Provider'}
      </Typography>

      <form onSubmit={handleSubmit} className={styles.form}>
        {!isEditMode && (
          <TextField
            fullWidth
            size="small"
            label="Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className={styles.input}
            required
            disabled={isLoading}
          />
        )}
        <TextField
          fullWidth
          size="small"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={styles.input}
          required
          disabled={isLoading}
        />
        <TextField
          fullWidth
          size="small"
          label="Description"
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className={styles.input}
          required
          disabled={isLoading}
        />

        {isEditMode && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className={styles.checkbox}
                disabled={isLoading}
              />
            }
            label="Is Active"
            className={styles.checkboxContainer}
          />
        )}

        <Box className={styles.actions}>
          <Button onClick={onClose} className={styles.cancelBtn} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            className={styles.submitBtn}
            disabled={(!isEditMode && (!code || !name || !description)) || (isEditMode && (!name || !description)) || isLoading}
          >
            {isEditMode ? 'Save' : 'Create'}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default CreateEditProviderModal;
